import { z } from 'zod';
import { optionalText, requiredText } from './field.js';
/**
 * The catalogue of relations between two contacts (rule 4 in `.claude/rules/contacts.md`).
 *
 * ## Direction
 *
 * `from` is the contact in whose record the fact is a property *of that
 * contact*; `to` is the counterpart. So a patient is the `from` of
 * `billing_recipient` (this patient has a billing recipient) and a child is
 * the `from` of `guardian` (this child has a guardian).
 *
 * The convention is not cosmetic: `is_exclusive` is enforced per
 * `from_contact_id`, so with it exclusivity always reads as "this contact has
 * at most one X". Without it the direction would decide what the flag means,
 * and the third exclusive type would get it wrong.
 *
 * `parent_of` is the deliberate exception: with kinship there is no side for
 * which the fact is more of a property, and "Elternteil von / Kind von" is the
 * more common reading direction.
 *
 * ## Labels
 *
 * `labelForward` is what the record of the `from` contact says about the `to`
 * contact, `labelInverse` the other way round. A symmetric type has no inverse
 * label and uses the forward one on both sides; the
 * `contact_relation_type_inverse_label` check constraint holds that.
 */
const relationTypeFields = {
    labelForward: requiredText(60),
    labelInverse: optionalText(60),
    /** Stored as one row regardless — two rows for one fact could drift apart.
     *  The domain normalizes the direction so the reverse duplicate collides. */
    isSymmetric: z.boolean().default(false),
    /** At most one relation of this type per `from` contact. */
    isExclusive: z.boolean().default(false),
    sortOrder: z.number().int().min(0).max(9999).default(0),
    active: z.boolean().default(true),
};
/** An inverse label is required exactly when the type is not symmetric. */
const symmetryRule = {
    check: (input) => (input.labelInverse !== null) === !input.isSymmetric,
    options: {
        message: 'labelInverse is required unless the type is symmetric',
        path: ['labelInverse'],
    },
};
export const contactRelationTypeInputSchema = z
    .object(relationTypeFields)
    .refine(symmetryRule.check, symmetryRule.options);
/**
 * Creating takes the same fields as editing. There is **no `code` field here
 * and no `isSystem`**, and that was still described as a derivation from
 * `labelForward` until the CLI slice — untrue since 0046, where an entry the
 * practitioner creates simply carries NULL.
 *
 * What the two absences amount to is that a system entry — the only kind whose
 * code carries meaning — cannot be created through this schema at all, which
 * `contact_relation_type_system_needs_code` then makes a refusal rather than a
 * habit. `guardian` and `billing_recipient` are written by `domain/tenant.ts`,
 * because they are part of what a tenant is.
 *
 * `ContactRelationTypeCreate` stays as an alias so the two intents still read
 * differently at the call sites.
 */
export const contactRelationTypeCreateSchema = contactRelationTypeInputSchema;
export const contactRelationTypeSchema = z.object({
    id: z.uuid(),
    /** Null on everything but a system entry, since 0046. The read detail shows
     *  it only where it is set, which is where the software greps for it. */
    code: z.string().nullable(),
    labelForward: z.string(),
    labelInverse: z.string().nullable(),
    isSymmetric: z.boolean(),
    isExclusive: z.boolean(),
    isSystem: z.boolean(),
    sortOrder: z.number().int(),
    active: z.boolean(),
});
/**
 * Logic is allowed to depend on the codes of system relation types and on
 * nothing else in the catalogue. This is the only one both sides read:
 * `guardian` is what the reminder about a minor without one looks for.
 *
 * `billing_recipient` had a constant here too and nothing ever imported it —
 * `domain/invoice-send.ts` declares its own, next to the query that uses it.
 * One definition beat two, and the one that survived is the one with a caller.
 */
export const GUARDIAN_RELATION_CODE = 'guardian';
//# sourceMappingURL=contact-relation-type.js.map